import React, { useState } from "react";
import { MdArrowBack, MdOutlineBackspace } from "react-icons/md";
import { BiInfoCircle } from "react-icons/bi";
import { AiOutlineIdcard } from "react-icons/ai";
import { IoPersonAddOutline } from "react-icons/io5";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { differenceInDays, format } from "date-fns";
import { useTranslation } from "react-i18next";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import { useOtherAccountDisplayName } from "../../../../../../utils/hooks/useOtherAccountDisplayName";
import useCourtPasses from "../../../hooks/useCourtPasses";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAAlert from "../../../../shared/EAAlert";
import EADivider from "../../../../shared/EADivider";
import EAChip from "../../../../shared/Chip/EAChip";
import EAModal from "../../../../shared/Modal/EAModal";
import EASkeletonLoader from "../../../../shared/EASkeletonLoader";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import {
  calculateRemainingEntrances,
  hasEntranceWarning,
  isOpenPass,
  isValidityEndingSoon,
} from "../../../../../utils/TicketPassHelper";
import "./PassSelection.scss";
import getId from "../../../../../../utils/getId";

const HtmlTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  })
);

interface IProps {
  exerciseDetails: Exercise | null;
  onPassSelected: (passId: string, assigned?: boolean) => void;
  onBack: () => void;
  alert?: string;
  loading?: boolean;
}

const getEntrancesLabel = (pass: Ticket): string => {
  if (isOpenPass(pass.passtype) || pass.entrances > 200) {
    return "bez limitu";
  }

  const remaining = calculateRemainingEntrances(pass);
  return `${remaining} z ${pass.entrances}`;
};

const PassCard: React.FC<{
  pass: Ticket;
  isAssigned: boolean;
  isLoading: boolean;
  onPassSelected: (passId: string, assigned?: boolean) => void;
  onShowDescription: (pass: Ticket) => void;
}> = ({ pass, isAssigned, isLoading, onPassSelected, onShowDescription }) => {
  const isDesktop = useDesktopSize();
  const otherAccountName = useOtherAccountDisplayName(pass.accountIds);
  const remaining = calculateRemainingEntrances(pass);
  const daysUntilExpiration = differenceInDays(pass.validto, new Date());
  const showLowEntrances = hasEntranceWarning(pass);
  const showExpiringSoon = isValidityEndingSoon(pass);
  const hasDescription = pass.description.length > 2;

  return (
    <div className="pass-card">
      <div className="pass-card__header">
        <div className="pass-card__info">
          <div className="pass-card__title">
            <AiOutlineIdcard className="pass-card__icon" />
            <span>{pass.name}</span>
            {hasDescription &&
              (isDesktop ? (
                <HtmlTooltip
                  placement="right"
                  arrow={true}
                  title={
                    <article
                      className="tooltip-description"
                      dangerouslySetInnerHTML={{ __html: pass.description }}
                    />
                  }
                >
                  <BiInfoCircle className="pass-card__tooltip-icon" />
                </HtmlTooltip>
              ) : (
                <BiInfoCircle className="pass-card__tooltip-icon" onClick={() => onShowDescription(pass)} />
              ))}
          </div>

          {(showLowEntrances || showExpiringSoon) && (
            <div className="pass-card__chips">
              {showLowEntrances && <EAChip label="Kończą się wejścia" variant="warning" />}
              {showExpiringSoon && (
                <EAChip
                  label={daysUntilExpiration <= 0 ? "Traci ważność" : `Wygasa za ${daysUntilExpiration} dni`}
                  variant="warning"
                />
              )}
            </div>
          )}

          <div className="pass-card__details">
            <span>
              Pozostało wejść: <strong>{getEntrancesLabel(pass)}</strong>
            </span>
            <span>
              Ważny od: <strong>{format(pass.validfrom, "dd.MM.yyyy")}</strong>
            </span>
            <span>
              Ważny do: <strong>{format(pass.validto, "dd.MM.yyyy")}</strong>
            </span>
            {otherAccountName && (
              <span>
                Uczestnik: <strong>{otherAccountName}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="pass-card__action">
        {isAssigned ? (
          <HtmlTooltip placement="top" title="Kliknij, aby wypisać się z tej rezerwacji.">
            <div>
              <EAButton
                title="Wypisz się"
                onClick={() => onPassSelected(getId(pass), true)}
                startIcon={<MdOutlineBackspace />}
                variant="outlined"
                color="secondary"
                disabled={isLoading}
              />
            </div>
          </HtmlTooltip>
        ) : (
          <EAButton
            title="Zapisz się tym karnetem"
            onClick={() => onPassSelected(getId(pass), false)}
            startIcon={<IoPersonAddOutline />}
            variant="outlined"
            disabled={isLoading || (!isOpenPass(pass.passtype) && remaining <= 0)}
          />
        )}
      </div>
    </div>
  );
};

const PassSelection: React.FC<IProps> = ({
  exerciseDetails,
  onPassSelected,
  onBack,
  alert,
  loading: passLoading = false,
}) => {
  const { t } = useTranslation();
  const { loading, getFilteredPasses } = useCourtPasses();
  const [selectedPassInfo, setSelectedPassInfo] = useState<Ticket | null>(null);

  if (!exerciseDetails) {
    return <EASkeletonLoader height="200px" />;
  }

  const availablePasses = getFilteredPasses(exerciseDetails.startTime);

  const formatTimeRange = (startTime: number, endTime: number) => {
    const startFormatted = format(startTime, "HH:mm");
    const endFormatted = format(endTime, "HH:mm");
    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <div className="pass-selection">
      <div className="pass-selection__summary">
        <h5>{exerciseDetails.exerciseGroup.displayName}</h5>
        <p>{formatTimeRange(exerciseDetails.startTime, exerciseDetails.closeTime)}</p>
      </div>

      {alert && <EAAlert type="error" message={alert} />}

      {loading ? (
        <EASkeletonLoader height="150px" />
      ) : availablePasses.length > 0 ? (
        <>
          <EADivider />
          <div className="pass-selection__list">
            {availablePasses.map((pass) => (
              <PassCard
                key={getId(pass)}
                pass={pass}
                isAssigned={exerciseDetails.assigned}
                isLoading={passLoading}
                onPassSelected={onPassSelected}
                onShowDescription={setSelectedPassInfo}
              />
            ))}
          </div>
        </>
      ) : (
        <EAAlert type="warning" message="Nie masz dostępnych karnetów do zapisania się na te zajęcia." />
      )}

      <div className="pass-selection__footer">
        <EAButton title="Powrót" onClick={onBack} startIcon={<MdArrowBack />} variant="text" fullWidth />
      </div>

      {selectedPassInfo && (
        <EAModal isOpen={!!selectedPassInfo} onClose={() => setSelectedPassInfo(null)} title={selectedPassInfo.name} hideButtons>
          {selectedPassInfo.description.length > 1 ? (
            <div dangerouslySetInnerHTML={{ __html: selectedPassInfo.description }} />
          ) : (
            <p>{t("brak_opisu")}</p>
          )}
        </EAModal>
      )}
    </div>
  );
};

export default PassSelection;
